import React, { useContext, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IDishesContext, DishesContext } from '../../../../dishesState/dishesContext';
import {
	ICopyMenuModalContext,
	CopyMenuModalContext,
} from '../copyMenuModalContext/copyMenuModalState/copyMenuModalContext';
import { UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

const RestaurantsMultipleSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { IDSelectedCustomer, IDSelectedRestaurant } = useContext<IDishesContext>(DishesContext);
	const { setIDsRestaurantsToCopyMenu, IDsSelectedRestaurantsToCopyMenu } =
		useContext<ICopyMenuModalContext>(CopyMenuModalContext);

	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);

	const restaurants = (() => {
		if (hasAccess) {
			return response?.data.restaurants.filter((el) => el.id !== IDSelectedRestaurant);
		}
		return loggedUser.restaurants?.filter((el) => el.id !== IDSelectedRestaurant);
	})();

	const handleRemove = (id: string) => {
		const updatedSelectedItems = IDsSelectedRestaurantsToCopyMenu.filter((el) => el !== id);
		setIDsRestaurantsToCopyMenu(updatedSelectedItems);
	};

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	const restaurantsList = useMemo(() => {
		return IDsSelectedRestaurantsToCopyMenu.map((el) => {
			const label = restaurants?.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, IDsSelectedRestaurantsToCopyMenu, restaurants]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					setIDsRestaurantsToCopyMenu={setIDsRestaurantsToCopyMenu}
					IDsSelectedRestaurantsToCopyMenu={IDsSelectedRestaurantsToCopyMenu}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default RestaurantsMultipleSelect;
